import { EvaEndpoint, IEvaServiceCallOptions } from "@springtree/eva-sdk-core-service";
import { IEvaServiceDefinition } from "@springtree/eva-services-core";
import { useCallback, useMemo } from "react";

import { useAuthContext } from "~/contexts/auth";
import Logger from "~/services/logger/logger";

export function useEvaService<T extends IEvaServiceDefinition>(serviceDefinition: new () => T) {
  const { currentUserToken, handleTokenExpired } = useAuthContext();
  const endpoint = useMemo(() => new EvaEndpoint(process.env.NEXT_PUBLIC_EVA_BACKEND_URL!), []);

  return useCallback(
    async (payload?: T["request"], options?: IEvaServiceCallOptions): Promise<T["response"]> => {
      try {
        // First attempt with current token
        return await endpoint.callService(serviceDefinition, payload, {
          authenticationToken: currentUserToken || undefined,
          ...options,
        });
      } catch (error: any) {
        // Check if it's a 401 error
        const is401 = error?.response?.status === 401 || error?.status === 401;

        if (is401) {
          Logger.instance.warn("useEvaService: Token expired (401), attempting token refresh...");

          try {
            const newToken = await handleTokenExpired();

            if (newToken) {
              Logger.instance.info("useEvaService: Token refreshed successfully, retrying original call");

              // Retry with new token
              return await endpoint.callService(serviceDefinition, payload, {
                authenticationToken: newToken,
                ...options,
              });
            }
            // If no token returned, fall through to re-throw
          } catch (refreshError) {
            Logger.instance.error("useEvaService: Token refresh failed", refreshError);
            // Fall through to re-throw the original error
          }
        }

        // Re-throw the original error if not 401 or refresh failed
        throw error;
      }
    },
    [currentUserToken, endpoint, serviceDefinition, handleTokenExpired]
  );
}
